/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: HotDeskingFormView.tsx
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Extensions module.
────────────────────────────────────────────────────────────────*/


import { Phone, Search, Copy, Plus } from 'lucide-react';
import { ModuleFormHeader } from '../../components/layouts';
import FixedActionBar from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { HotDeskingDeviceInfoBox } from './components/HotDeskingDeviceInfoBox';
import { HotDeskingQuickListModal } from './components/HotDeskingQuickListModal';
import { useHotDeskingFormView } from './hooks/useHotDeskingFormView';

export default function HotDeskingFormView() {
    const {
        id, isEditMode,
        formData, errors, isSaving, isLoading, isLoadingOptions,
        sipProfiles, dispatchLocations,
        handleChange, handleSaveWithAction, handleGeneratePassword,
        handleCancelForm, handleGoToList, handleAddNew, handleDuplicateFromForm,
        showCancelModal, setShowCancelModal, confirmCancel,
        showDeleteModal, isDeleting, handleDeleteClick, handleDeleteConfirm, setShowDeleteModal,
        showQuickList, quickListDevices, quickListSearch, isSearching, isLoadingInitial,
        setShowQuickList, setQuickListSearch, closeQuickList,
        buildTooltipContent, t,
    } = useHotDeskingFormView();

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
        );
    }

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<Phone className="h-5 w-5 text-primary" />}
                title={isEditMode ? `${t('hotDesking.editTitle')} - ${formData.username || ''}` : t('hotDesking.createTitle')}
                backButtonText={t('hotDesking.actions.list')}
                onBack={handleGoToList}
                actions={[
                    { icon: <Search className="h-5 w-5 text-muted-foreground" />, tooltip: t('hotDesking.actions.quickList'), onClick: () => setShowQuickList(true) },
                    ...(isEditMode ? [{ icon: <Copy className="h-5 w-5 text-muted-foreground" />, tooltip: t('hotDesking.actions.duplicate'), onClick: handleDuplicateFromForm }] : []),
                    { icon: <Plus className="h-5 w-5 text-muted-foreground" />, tooltip: t('hotDesking.actions.add'), onClick: handleAddNew },
                ]}
            />

            <div className="mt-8">
                <form onSubmit={(e) => { e.preventDefault(); }} className="space-y-4">
                    <HotDeskingDeviceInfoBox
                        formData={formData}
                        errors={errors}
                        isEditMode={isEditMode}
                        isLoadingOptions={isLoadingOptions}
                        sipProfiles={sipProfiles}
                        dispatchLocations={dispatchLocations}
                        buildTooltipContent={buildTooltipContent}
                        onChange={handleChange}
                        onGeneratePassword={handleGeneratePassword}
                    />
                </form>
            </div>

            <FixedActionBar
                onSaveWithAction={handleSaveWithAction}
                onCancel={handleCancelForm}
                onDelete={handleDeleteClick}
                isEditMode={isEditMode}
                isSaving={isSaving}
                cancelText={t('common.cancel')}
                deleteText={t('common.delete')}
                useSaveDropdown={true}
                saveDropdownStorageKey="hotDesking:saveAction"
                saveDisabled={isSaving}
            />

            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteConfirm}
                title={t('hotDesking.deleteModal.title')}
                message={t('hotDesking.deleteModal.message', { username: formData.username || formData.description || 'device' })}
                confirmText={t('common.delete')}
                cancelText={t('common.cancel')}
                type="danger"
                isLoading={isDeleting}
            />

            <ConfirmationModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={confirmCancel}
                title={t('common.confirmCancel', 'Discard Changes?')}
                message={t('hotDesking.messages.confirmCancel', 'Are you sure you want to discard your changes?')}
                confirmText={t('common.discard', 'Discard')}
                cancelText={t('common.keepEditing', 'Keep Editing')}
                type="warning"
            />

            <HotDeskingQuickListModal
                isOpen={showQuickList}
                currentItemId={id}
                devices={quickListDevices}
                isLoadingInitial={isLoadingInitial}
                isSearching={isSearching}
                searchValue={quickListSearch}
                onSearchChange={setQuickListSearch}
                onClose={closeQuickList}
            />
        </div>
    );
}
